const BACKEND_STREAM_URL = "https://optibot-procurement.onrender.com/chat_with_documents";

class ActionProvider {
  static agencyFilter = "";
  static naicsFilter = "";

  constructor(createChatBotMessage, setStateFunc, createClientMessage) {
    this.createChatBotMessage = createChatBotMessage;
    this.setState = setStateFunc;
    this.createClientMessage = createClientMessage;

    this.eventSource = null;

    // track current response
    this._messageId = null;   // id of the bot message we actually render
    this._isDone = false;
    this._chunks = 0;
    this._buffer = "";
  }

  setFilters = (agencyFilter, naicsFilter) => {
    ActionProvider.agencyFilter = agencyFilter;
    ActionProvider.naicsFilter = naicsFilter;
  };

  _resetSession = () => {
    if (this.eventSource) this.eventSource.close();
    this.eventSource = null;
    this._messageId = null;
    this._isDone = false;
    this._chunks = 0;
    this._buffer = "";
  };

  handleSendMessage = (message) => {
    // start fresh
    this._resetSession();

    // Build URL
    const url = new URL(BACKEND_STREAM_URL);
    url.searchParams.append("question", message);
    url.searchParams.append("session_id", `session_${Date.now()}`);
    if (ActionProvider.agencyFilter) url.searchParams.append("agency_filter", ActionProvider.agencyFilter);
    if (ActionProvider.naicsFilter) url.searchParams.append("naics_filter", ActionProvider.naicsFilter);

    // Open SSE
    this.eventSource = new EventSource(url.toString());

    this.eventSource.onmessage = (event) => {
      if (this._isDone) return; // ignore late events
      let data;
      try { data = JSON.parse(event.data); } catch { return; }

      // console.debug("SSE:", data); // uncomment to inspect

      if (data.type === "chunk") {
        this._chunks += 1;
        this._buffer += data.content;

        if (!this._messageId) {
          // first time we got content: create a bot message (no "loading" flag)
          const botMessage = this.createChatBotMessage(this._buffer);
          this._messageId = botMessage.id;
          this._addMessage(botMessage);
        } else {
          this._updateMessage(this._messageId, this._buffer);
        }

      } else if (data.type === "no_records") {
        // create final message immediately
        this._isDone = true;
        if (!this._messageId) {
          const botMessage = this.createChatBotMessage("No records found.");
          this._messageId = botMessage.id;
          this._addMessage(botMessage);
        } else {
          this._updateMessage(this._messageId, "No records found.");
        }
        this._closeSSE();

      } else if (data.type === "error") {
        this._isDone = true;
        const msg = `❌ Error: ${data.content || "Unknown error."}`;
        if (!this._messageId) {
          const botMessage = this.createChatBotMessage(msg);
          this._messageId = botMessage.id;
          this._addMessage(botMessage);
        } else {
          this._updateMessage(this._messageId, msg);
        }
        this._closeSSE();

      } else if (data.type === "done") {
        this._isDone = true;

        // If nothing arrived, emit a normal message now
        if (!this._messageId) {
          const text = this._chunks === 0 && !this._buffer.trim()
            ? "No records found."
            : (this._buffer || "No records found.");
          const botMessage = this.createChatBotMessage(text);
          this._messageId = botMessage.id;
          this._addMessage(botMessage);
        } else {
          // finalize to current buffer (never flip to any loading UI)
          this._updateMessage(this._messageId, this._buffer || "No records found.");
        }
        this._closeSSE();
      }
    };

    this.eventSource.onerror = (err) => {
      // Network/host issue — render a normal message and stop.
      if (!this._isDone) {
        this._isDone = true;
        const msg = "❌ Connection error. Please check your internet connection and try again.";
        if (!this._messageId) {
          const botMessage = this.createChatBotMessage(msg);
          this._messageId = botMessage.id;
          this._addMessage(botMessage);
        } else {
          this._updateMessage(this._messageId, msg);
        }
      }
      this._closeSSE();
      console.error("EventSource error:", err);
    };
  };

  _closeSSE = () => {
    if (this.eventSource) {
      try { this.eventSource.close(); } catch {}
      this.eventSource = null;
    }
  };

  _addMessage = (message) => {
    this.setState((prev) => ({
      ...prev,
      messages: [...prev.messages, message],
    }));
  };

  _updateMessage = (id, content) => {
    this.setState((prev) => {
      const messages = [...prev.messages];
      const idx = messages.findIndex((m) => m.id === id);
      if (idx === -1) return prev;
      messages[idx] = { ...messages[idx], message: content };
      return { ...prev, messages };
    });
  };
}

export default ActionProvider;
